/* eslint-disable react/no-danger */
import React, { useState, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "primereact/button";
import "react-datepicker/dist/react-datepicker.css";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputMask } from "primereact/inputmask";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { AutoComplete } from "primereact/autocomplete";

import LISTA_CARGOS from "components/RH/inputs/cargos";
import CargoAutocomplete from "components/RH/inputs/inputCargo";
import InputDateSomentePicker from "components/RH/inputs/InputDateSomentePicker";
import InputDate from "components/RH/inputs/InputDate";

import ptBR from "date-fns/locale/pt-BR";
import { parse, format } from "date-fns";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskEnum from "utils/MaskEnum";
import MaskUtil from "utils/MaskUtil";
import { validarCPF } from "utils/ValidacaoUtils";
import { parseDate } from "utils/DateValidationUtils";

registerLocale("pt-BR", ptBR);

const ConviteFuncionario = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const notify = useNotification();
  const { empresaId } = useParams();
  const { setLoading } = useLoading();

  const Requicicao = new Api();

  const [conviteTexto, setConviteTexto] = useState("");
  const [showModal, setShowModal] = useState(false);

  const { TipoEscala } = MaskEnum;

  // Lista de horários de trabalho sugeridos
  const LISTA_HORARIOS = [
    "08:00 - 18:00",
    "09:00 - 19:00",
    "07:00 - 17:00",
    "12:00 - 22:00",
    "14:00 - 22:00",
    "06:00 - 14:00",
    "22:00 - 06:00",
  ];

  const [dados, setDados] = useState({
    nome: "",
    cpf: "",
    dataNascimento: null,
    email: "",
    telefone: "",
    regimeContratacaoId: null,
    jornadaTrabalhoId: null,
    salario: "",
    cargo: "",
    horarioTrabalho: "",
    dataAdmissao: null,
    tipoEscala: null
  });

  const [regimes, setRegimes] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [horarioSuggestions, setHorarioSuggestions] = useState([]);

  // Estados para validação visual dos campos obrigatórios
  const [errors, setErrors] = useState({});
  
  // Lista de campos obrigatórios
  const camposObrigatorios = [
    "nome",
    "cpf", 
    "dataNascimento",
    "email",
    "telefone",
    "regimeContratacaoId",
    "jornadaTrabalhoId",
    "salario",
    "cargo",
    "horarioTrabalho",
    "dataAdmissao",
    "tipoEscala"
  ];

  // Função para verificar se um campo é obrigatório
  const isRequired = (campo) => camposObrigatorios.includes(campo);
  
  // Função para verificar se um campo tem erro
  const isError = (campo) => errors && errors[campo];
  
  const validarMaiorDe16 = (dataNascimento) => {
    // Verifica se a data foi fornecida
    if (!dataNascimento) {
      return false;
    }

    // Usa parseDate para converter a string no formato dd/mm/yyyy para Date
    const nascimento = parseDate(dataNascimento);
    
    // Verifica se a data é válida
    if (!nascimento) {
      return false;
    }

    const hoje = new Date();

    // Verifica se a data não é futura
    if (nascimento > hoje) {
      return false;
    }

    // Calcula a diferença de anos entre a data atual e a data de nascimento
    let idade = hoje.getFullYear() - nascimento.getFullYear();

    // Verifica se ainda não fez aniversário este ano
    const aniversarioNaoPassou =
      hoje.getMonth() < nascimento.getMonth() ||
      (hoje.getMonth() === nascimento.getMonth() &&
        hoje.getDate() < nascimento.getDate());

    if (aniversarioNaoPassou) {
      idade -= 1; // Se o aniversário ainda não passou, subtrai 1 da idade
    }

    return idade >= 16;
  };

  // Função para obter mensagem de erro específica
  const getErrorMessage = (campo) => {
    if (!errors || !errors[campo]) return "";
    
    // Mensagens específicas para cada campo
    if (campo === "dataNascimento") {
      if (!dados.dataNascimento) {
        return "Obrigatório";
      }
      
      // Se o campo está preenchido mas tem erro, é problema de idade
      if (errors[campo]) {
        if (!validarMaiorDe16(dados.dataNascimento)) {
          return "Idade deve ser maior que 16 anos";
        }
      }
    }
    
    return "Obrigatório";
  };

  // Função para validar campos obrigatórios
  const validarCamposObrigatorios = () => {
    const newErrors = {};
    
    camposObrigatorios.forEach((campo) => {
      if (!dados[campo] || dados[campo] === "") {
        newErrors[campo] = true;
      }
    });

    // Validações específicas
    if (dados.cpf && !validarCPF(dados.cpf.replace(/\D/g, ""))) {
      newErrors.cpf = true;
    }
    
    // Para data de nascimento, valida se está vazio OU se a idade é insuficiente
    if (!dados.dataNascimento) {
      newErrors.dataNascimento = true;
    } else if (!validarMaiorDe16(dados.dataNascimento)) {
      newErrors.dataNascimento = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const VisualizarConvite = async () => {
    try {
      setLoading(true);
      const response = await Requicicao.Get({
        endpoint: `/Funcionarios/Convite/${dados.nome}/${empresaId}`,
        config: auth.GetHeaders()
      });

      if (response.codigoEstadoHttp === 200) {
        setConviteTexto(response.mensagemAdicional);
        setShowModal(true);
      } else {
        notify({
          type: "error",
          message: "Erro ao carregar o convite. Tente novamente mais tarde."
        });
      }
    } catch (error) {
      console.error("Erro ao visualizar o convite:", error);
      notify({
        type: "error",
        message: "Erro ao visualizar o convite. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  };

  const HandleChange = (event) => {
    const { name, value } = event.target;
    setDados((prevState) => ({ ...prevState, [name]: value }));
    
    // Limpa o erro do campo quando o usuário começa a digitar
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const HandleDropdownChange = (e, field) => {
    setDados((prevState) => ({ ...prevState, [field]: e.value }));
    
    // Limpa o erro do campo quando o usuário seleciona uma opção
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const validarDados = () => {
    const erros = [];
    
    if (!dados.nome) {
      erros.push("Nome é obrigatório");
    }
    
    if (!dados.cpf) {
      erros.push("CPF é obrigatório");
    } else if (!validarCPF(dados.cpf)) {
      erros.push("CPF inválido");
    }
    
    if (!dados.dataNascimento) {
      erros.push("Data de nascimento é obrigatória");
    } else if (!validarMaiorDe16(dados.dataNascimento)) {
      erros.push("Idade deve ser maior que 16 anos");
    }
    if (!dados.email) erros.push("E-mail é obrigatório");
    if (!dados.telefone) erros.push("Telefone é obrigatório");
    if (!dados.regimeContratacaoId)
      erros.push("Regime de Contratação é obrigatório");
    if (!dados.jornadaTrabalhoId)
      erros.push("Jornada de Trabalho é obrigatória");
    if (!dados.salario) erros.push("Salário é obrigatório");
    if (!dados.cargo) erros.push("Cargo é obrigatório");
    if (!dados.tipoEscala) erros.push("Tipo Escala é obrigatório"); // Adicionada validação
    if (!dados.horarioTrabalho) erros.push("Horário de Trabalho é obrigatório");
    if (!dados.dataAdmissao) {
      erros.push("Data de Admissão é obrigatória");
    }

    return erros;
  };

  const HandleSubmit = async (event) => {
    event.preventDefault();

    // Valida campos obrigatórios antes de prosseguir
    const validacaoVisual = validarCamposObrigatorios();
    
    // Verifica se há erros específicos de idade
    const erros = validarDados();
    
    if (erros.length > 0) {
      notify({
        type: "error",
        message: `Por favor, corrija os seguintes erros antes de enviar: <br/> * ${erros.join("<br/>* ")}`
      });
      return;
    }
    
    if (!validacaoVisual) {
      notify({
        type: "error",
        message: "Por favor, preencha todos os campos obrigatórios."
      });
      return;
    }

    try {
      setLoading(true);
      const formattedCpf = dados.cpf ? dados.cpf.replace(/\D/g, "") : ""; // Remove formatação do CPF
      const formattedTelefone = dados.telefone
        ? dados.telefone.replace(/\D/g, "")
        : ""; // Remove formatação do telefone
      const formattedSalario = dados.salario
        ? parseFloat(
            dados.salario
              .toString()
              .replace(/[^0-9,-]+/g, "")
              .replace(",", ".")
          )
        : 0; // Converte o salário para número

      // Converte datas para ISO
      let dataNascimentoISO = null;
      if (dados.dataNascimento) {
        try {
          dataNascimentoISO = format(
            parse(dados.dataNascimento, "dd/MM/yyyy", new Date()),
            "yyyy-MM-dd'T'00:00:00"
          );
        } catch (e) {
          dataNascimentoISO = null;
        }
      }
      let dataAdmissaoISO = null;
      if (dados.dataAdmissao) {
        try {
          dataAdmissaoISO = format(
            parse(dados.dataAdmissao, "dd/MM/yyyy", new Date()),
            "yyyy-MM-dd'T'00:00:00"
          );
        } catch (e) {
          dataAdmissaoISO = null;
        }
      }

      const payload = {
        ...dados,
        empresaId,
        cpf: formattedCpf,
        telefone: formattedTelefone,
        salario: formattedSalario,
        dataNascimento: dataNascimentoISO,
        dataAdmissao: dataAdmissaoISO
      };

      // Supondo que os demais processos de validação e envio estão corretos
      await Requicicao.Post({
        endpoint: "/Funcionarios/EnviarConvite",
        data: payload,
        config: auth.GetHeaders()
      });

      notify({
        type: "success",
        message: "Convite enviado com sucesso."
      });
      navigate(-1);
    } catch (error) {
      console.error("Erro ao enviar o convite:", error);
      notify({
        type: "error",
        message: "Erro ao enviar o convite. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  };

  const buscarHorarios = (event) => {
    setHorarioSuggestions(
      LISTA_HORARIOS.filter((h) =>
        h.toLowerCase().includes(event.query.toLowerCase())
      )
    );
  };

  useEffect(() => {
    const CarregarRegimes = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Regime",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedRegimes = response.objetoResposta
            .map((regime) => ({
              label: regime.descricao,
              value: regime.id
            }))
            .filter((x) => x.label !== "CLT Flex");
          setRegimes(formattedRegimes);
        }
      } catch (error) {
        console.error("Erro ao carregar regimes de contratação:", error);
      }
    };

    const CarregarJornadas = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Jornada",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedJornadas = response.objetoResposta.map((jornada) => ({
            label: jornada.descricao,
            value: jornada.id
          }));
          setJornadas(
            formattedJornadas.filter((x) => !x.label.includes("Escala"))
          );
        }
      } catch (error) {
        console.error("Erro ao carregar jornadas de trabalho:", error);
      }
    };

    CarregarRegimes();
    CarregarJornadas();
  }, [auth]);

  return (
    <div className="card" id="form-convite-funcionario">
      <form className="card-body" onSubmit={HandleSubmit}>
        <div className="row g-3">
          <div className="col-md-4">
            <label htmlFor="nome" className="form-label">
              Nome Completo{isRequired("nome") && <span className="text-danger">*</span>}
            </label>
            <InputText
              id="nome"
              name="nome"
              value={dados.nome}
              onChange={HandleChange}
              className={`form-control${isError("nome") ? " is-invalid" : ""}`}
            />
            {isError("nome") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="cpf" className="form-label">
              CPF{isRequired("cpf") && <span className="text-danger">*</span>}
            </label>
            <InputText
              id="cpf"
              name="cpf"
              value={MaskUtil.applyCpfMask(dados.cpf || "")}
              onChange={(e) => setDados((prev) => ({ ...prev, cpf: MaskUtil.applyCpfMask(e.target.value) }))}
              className={`form-control${isError("cpf") ? " is-invalid" : ""}`}
              placeholder="CPF"
            />
            {isError("cpf") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="dataNascimento" className="form-label">
              Data de Nascimento{isRequired("dataNascimento") && <span className="text-danger">*</span>}
            </label>
            <InputDate
              id="dataNascimento"
              name="dataNascimento"
              value={dados.dataNascimento}
              onChange={(dateString) => setDados((prev) => ({ ...prev, dataNascimento: dateString }))}
              placeholder="dd/mm/aaaa"
              className={`form-control${isError("dataNascimento") ? " is-invalid" : ""}`}
            />
            {isError("dataNascimento") && <div className="invalid-feedback">{getErrorMessage("dataNascimento")}</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="email" className="form-label">
              E-mail{isRequired("email") && <span className="text-danger">*</span>}
            </label>
            <InputText
              id="email"
              name="email"
              value={dados.email}
              onChange={HandleChange}
              className={`form-control${isError("email") ? " is-invalid" : ""}`}
              keyfilter="email"
            />
            {isError("email") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="telefone" className="form-label">
              Telefone com DDD{isRequired("telefone") && <span className="text-danger">*</span>}
            </label>
            <InputMask
              id="telefone"
              mask="(99) 99999-9999"
              name="telefone"
              value={dados.telefone}
              onChange={HandleChange}
              className={`form-control${isError("telefone") ? " is-invalid" : ""}`}
            />
            {isError("telefone") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="regimeContratacaoId" className="form-label">
              Regime de Contratação{isRequired("regimeContratacaoId") && <span className="text-danger">*</span>}
            </label>
            <Dropdown
              id="regimeContratacaoId"
              value={dados.regimeContratacaoId}
              onChange={(e) => HandleDropdownChange(e, "regimeContratacaoId")}
              options={regimes}
              placeholder="Selecione o Regime de Contratação"
              className={`w-100${isError("regimeContratacaoId") ? " is-invalid" : ""}`}
            />
            {isError("regimeContratacaoId") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="jornadaTrabalhoId" className="form-label">
              Jornada de Trabalho{isRequired("jornadaTrabalhoId") && <span className="text-danger">*</span>}
            </label>
            <Dropdown
              id="jornadaTrabalhoId"
              value={dados.jornadaTrabalhoId}
              onChange={(e) => HandleDropdownChange(e, "jornadaTrabalhoId")}
              options={jornadas}
              placeholder="Selecione a Jornada de Trabalho"
              className={`w-100${isError("jornadaTrabalhoId") ? " is-invalid" : ""}`}
            />
            {isError("jornadaTrabalhoId") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="salario" className="form-label">
              Salário{isRequired("salario") && <span className="text-danger">*</span>}
            </label>
            <InputNumber
              id="salario"
              name="salario"
              mode="currency"
              locale="pt-BR"
              currency="BRL"
              value={dados.salario}
              onChange={(e) =>
                HandleChange({
                  target: {
                    name: "salario",
                    value: e.value
                  }
                })
              }
              className="w-100"
              inputClassName={`form-control${isError("salario") ? " is-invalid" : ""}`}
            />
            {isError("salario") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="cargo" className="form-label">
              Cargo{isRequired("cargo") && <span className="text-danger">*</span>}
            </label>
            <CargoAutocomplete
              value={dados.cargo}
              onChange={(valor) =>
                setDados((prev) => ({ ...prev, cargo: valor }))
              }
              placeholder="Digite ou selecione o cargo"
              className={isError("cargo") ? "is-invalid" : ""}
            />
            {isError("cargo") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="dataAdmissao" className="form-label">
              Data de Admissão{isRequired("dataAdmissao") && <span className="text-danger">*</span>}
            </label>
            <InputDate
              id="dataAdmissao"
              name="dataAdmissao"
              value={dados.dataAdmissao}
              onChange={(dateString) => setDados((prev) => ({ ...prev, dataAdmissao: dateString }))}
              placeholder="dd/mm/aaaa"
              className={`form-control${isError("dataAdmissao") ? " is-invalid" : ""}`}
            />
            {isError("dataAdmissao") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="horarioTrabalho" className="form-label">
              Horário de Trabalho{isRequired("horarioTrabalho") && <span className="text-danger">*</span>}
            </label>
            <AutoComplete
              id="horarioTrabalho"
              value={dados.horarioTrabalho}
              suggestions={horarioSuggestions}
              completeMethod={buscarHorarios}
              onChange={(e) => setDados((prev) => ({ ...prev, horarioTrabalho: e.value }))}
              className="w-100"
              inputClassName={`form-control${isError("horarioTrabalho") ? " is-invalid" : ""}`}
              placeholder="Digite ou selecione o horário"
              dropdown
            />
            {isError("horarioTrabalho") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="tipoEscala" className="form-label">
              Tipo Escala{isRequired("tipoEscala") && <span className="text-danger">*</span>}
            </label>
            <Dropdown
              id="tipoEscala"
              value={dados.tipoEscala}
              onChange={(e) => HandleDropdownChange(e, "tipoEscala")}
              options={TipoEscala}
              className={`w-100${isError("tipoEscala") ? " is-invalid" : ""}`}
            />
            {isError("tipoEscala") && <div className="invalid-feedback">Obrigatório</div>}
          </div>
        </div>
        <div className="d-flex justify-content-end mt-4 mb-4">
          <Button
            type="button"
            className="btn btn-info ms-2 me-4"
            onClick={VisualizarConvite}
            icon="pi pi-eye"
            label="Visualizar Convite"
          />

          <Button
            type="button"
            className="btn btn-secondary me-4"
            onClick={() => navigate(-1)}
          >
            Voltar
          </Button>
          <Button type="submit" className="btn btn-primary ms-2">
            Enviar Convite
          </Button>
        </div>
        {showModal && (
          <Dialog
            header="Convite"
            visible={showModal}
            style={{ width: "50vw" }}
            modal
            onHide={() => setShowModal(false)}
          >
            <div dangerouslySetInnerHTML={{ __html: conviteTexto }} />
            <Button
              type="button"
              className="btn btn-secondary mt-3"
              onClick={() => setShowModal(false)}
            >
              Fechar
            </Button>
          </Dialog>
        )}
      </form>
    </div>
  );
};

export default ConviteFuncionario;
